import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CustomAlert from '../CustomAlert';

const apiUrl = process.env.REACT_APP_API_URL;

const EMPTY = { name: '', description: '', tax_id: '' };

/**
 * Category edit form. Same shape as CategoryAdd: no page chrome, so it sits
 * either in the Drawer on the list page or standalone on /categories/edit/:id.
 *
 * id — from props in the drawer, from the route param standalone
 * onSuccess(updated) — drawer closes and the list refreshes
 */
export default function CategoryEdit({ id: idProp, onSuccess }) {
  const params = useParams();
  const id = idProp ?? params.id;
  const navigate = useNavigate();

  const [category, setCategory] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ isOpen: false, type: 'error', title: '', message: '' });

  const closeAlert = () => setAlert((a) => ({ ...a, isOpen: false }));

  useEffect(() => {
    const token = localStorage.getItem('token');

    fetch(`${apiUrl}/categories/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch category');
        return res.json();
      })
      // NULL columns would flip the inputs to uncontrolled — keep '' for those.
      .then((data) =>
        setCategory({
          ...EMPTY,
          ...Object.fromEntries(Object.entries(data).filter(([, v]) => v !== null)),
        })
      )
      .catch((err) => {
        console.error(err);
        setAlert({
          isOpen: true,
          type: 'error',
          title: 'Грешка',
          message: 'Неуспешно вчитување на категоријата.',
        });
      });
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCategory((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category.name.trim()) {
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Недостасува име',
        message: 'Името на категоријата е задолжително.',
      });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/categories/edit/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: category.name.trim(),
          description: category.description,
          tax_id: category.tax_id,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to update category');

      if (onSuccess) {
        onSuccess(data);
      } else {
        navigate('/categories/list');
      }
    } catch (error) {
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Грешка',
        message: error.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700">Назив *</label>
          <input
            type="text"
            name="name"
            value={category.name}
            onChange={handleChange}
            required
            className="mt-1 block w-full border border-gray-300 rounded-md p-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-green-600"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Данок</label>
          <select
            name="tax_id"
            value={category.tax_id}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md p-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-green-600"
          >
            <option value="">Не е дефиниран</option>
            <option value="0">0%</option>
            <option value="5">5%</option>
            <option value="18">18%</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Опис</label>
          <textarea
            name="description"
            rows={4}
            value={category.description}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md p-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-green-600"
          />
        </div>

        <div className="pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={saving}
            className="w-full px-4 py-2.5 bg-green-500 hover:bg-green-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-colors"
          >
            {saving ? 'Се зачувува...' : 'Зачувај промени'}
          </button>
        </div>
      </form>

      <CustomAlert
        isOpen={alert.isOpen}
        onClose={closeAlert}
        type={alert.type}
        title={alert.title}
        message={alert.message}
      />
    </>
  );
}
